import { keepPreviousData, useQueries, UseQueryOptions } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { CreditNotesByMakerOrg, fetchCreditNotes, extractMarketsWithCreditNotes } from './useCreditNotes';
import { fetchTakerMarketsWithoutUserSuppliers, TakerMarket } from './useTakerMarkets';

// TODO: check which fields are actually nullable/optional
interface TakerHistoryResponseItem {
  // Core required fields
  uuid: string;
  payDate: string;
  depositDate: string;
  currency: string;
  takerId: number;
  takerDivisionName: string;
  makerOrganizationName: string;
  makerOrganizationUuid: string;
  isPendingClear: boolean;
  isPendingMarketClear: boolean;
  id: number | null;

  // Cleared data (always available)
  clearedAprWeightedAvg: number;
  clearedDiscountWeightedAvg: number;
  clearedDiscountedAmount: number;
  clearedDpeWeightedAvg: number;
  clearedEarn: number;
  clearedInvoiceAmount: number;
  clearedInvoiceCount: number;

  // Optional fields - may not be available in all APIs (SEA vs Legacy)
  clearedDiscountWeightedSum?: number;
  clearedYield?: number;
  hasCreditNote?: boolean;
  snapshotComplete?: boolean;
  totalInvoiceCount?: number;

  // Accepted data (optional - legacy only)
  acceptedAprWeightedAvg?: number;
  acceptedDiscountWeightedAvg?: number;
  acceptedDiscountedAmount?: number;
  acceptedDpeWeightedAvg?: number;
  acceptedEarn?: number;
  acceptedInvoiceAmount?: number;
  acceptedInvoiceCount?: number;
  acceptedYield?: number;

  // Awarded data (optional - legacy only)
  awardedAprWeightedAvg?: number | null;
  awardedDiscountWeightedAvg?: number | null;
  awardedDiscountedAmount?: number;
  awardedDpeWeightedAvg?: number | null;
  awardedEarn?: number;
  awardedInvoiceAmount?: number;
  awardedInvoiceCount?: number;
  awardedYield?: number;

  // Not accepted data (optional - legacy only)
  notAcceptedAprWeightedAvg?: number | null;
  notAcceptedDiscountWeightedAvg?: number | null;
  notAcceptedDiscountedAmount?: number;
  notAcceptedDpeWeightedAvg?: number | null;
  notAcceptedEarn?: number;
  notAcceptedInvoiceAmount?: number;
  notAcceptedInvoiceCount?: number;
  notAcceptedYield?: number;

  // Min/Max statistics (optional - legacy only)
  maxAcceptedApr?: number;
  maxAcceptedDiscount?: number;
  maxAcceptedDpe?: number;
  maxAwardedApr?: number | null;
  maxAwardedDiscount?: number | null;
  maxAwardedDpe?: number | null;
  maxClearedApr?: number;
  maxClearedDiscount?: number;
  maxClearedDpe?: number;
  minAcceptedApr?: number;
  minAcceptedDiscount?: number;
  minAcceptedDpe?: number;
  minAwardedApr?: number | null;
  minAwardedDiscount?: number | null;
  minAwardedDpe?: number | null;
  minClearedApr?: number;
  minClearedDiscount?: number;
  minClearedDpe?: number;

  // Adjustment and covering data (optional - legacy only)
  coveringInvoiceAmount?: number;
  coveringInvoiceCount?: number;
  matchedAdjustmentAmount?: number;
  matchedAdjustmentCount?: number;
  totalAdjustmentAmount?: number;
  totalAdjustmentCount?: number;
  unmatchedAdjustmentAmount?: number;
  unmatchedAdjustmentCount?: number;
}

export interface TakerHistory extends TakerHistoryResponseItem {
  /**
   * Alias for `uuid`
   */
  marketId: string;
  marketUuid: string;
  takerUuid: string;
  dsfFundingStatus?: SEADsfFundingStatus;
  isInstantPay: boolean;
  statementIds: string[];
}

export interface TakerHistoryArgs {
  // chartData?: boolean;
  startDate: string;
  endDate: string;
  currency?: string; // Optional - when provided, the clear amounts are returned converted to this currency, when not provided, the clear amounts are returned in the original currency
  page?: number;
  limit?: number;
  sort: string;
  pendingClear?: boolean;
  makerOrganizationUuids?: string[] | undefined;
  dateType?: 'payDate' | 'depositDate';
}

export type SEASupplierMarketClearsParams = paths['/supplier-market-clears']['get']['parameters']['query'];
export type SEASupplierMArketClearsSortParams = NonNullable<SEASupplierMarketClearsParams>['sort'];
export type SEASupplierMarketClearsResponse =
  paths['/supplier-market-clears']['get']['responses']['200']['content']['application/json'];
export type SEADsfFundingStatus =
  paths['/supplier-market-clears']['get']['responses']['200']['content']['application/json']['data']['clears'][0]['dsfFundingStatus'];

// https://fss-gateway.c2fo.com/usercast/api/c2fo/user/taker/history?currency=USD&filter=%7B%22isPendingClear__eq%22:false,%22payDate__between%22:%5B%222023-04-01,2023-06-30%22%5D%7D&page=%7B%22page%22:1,%22limit%22:100%7D&sort=payDate

/**
 * Transform supplier market clears data to TakerHistory format
 * Provides default values for fields not available in the supplier market clears API
 */
const transformSupplierMarketClearToTakerHistory = (
  item: SupplierMarketHistory,
  takerMarket: TakerMarket | undefined,
  marketsWithCreditNotes: string[]
): TakerHistory => {
  return {
    // Fields available from supplier market clears API
    uuid: item.uuid,
    payDate: item.payDate,
    depositDate: item.depositDate,
    clearedInvoiceAmount: item.clearedInvoiceAmount,
    clearedDiscountedAmount: item.clearedDiscountedAmount,
    clearedEarn: item.clearedEarn,
    clearedDiscountWeightedAvg: item.clearedDiscountWeightedAvg,
    clearedAprWeightedAvg: item.clearedAprWeightedAvg,
    clearedDpeWeightedAvg: item.clearedDpeWeightedAvg,
    clearedInvoiceCount: item.clearedInvoiceCount,
    isPendingClear: item.isPendingClear,
    isPendingMarketClear: item.isPendingMarketClear,
    currency: item.currency,
    makerOrganizationName: item.supplierMarket.makerOrganizationName,
    makerOrganizationUuid: item.supplierMarket.makerOrganizationUuid,
    marketUuid: item.supplierMarket.marketUuid,
    takerUuid: item.supplierMarket.supplierDivisionUuid,
    marketId: item.supplierMarket.marketUuid, // that's what the download endpoint and app navigation expects
    dsfFundingStatus: item.dsfFundingStatus,
    id: item.clearIds?.[0], // We know API returns clear ids. fill it for now. we dont use this anywhere
    hasCreditNote: marketsWithCreditNotes.includes(item.supplierMarket.marketUuid),
    isInstantPay: item.isInstantPay,
    statementIds: item.statementIds,

    // enrich below data from useTakerMarkets hook
    takerDivisionName: takerMarket?.takerDivisionName || '',
    takerId: takerMarket?.takerDivisionId || 0,
  };
};

export type SupplierMarketHistory = SEASupplierMarketClearsResponse['data']['clears'][0];
type RawTakerHistoryData = TakerHistory | SupplierMarketHistory;

export const fetchTakerHistory = async ({
  startDate,
  endDate,
  page,
  limit,
  makerOrganizationUuids,
  pendingClear,
  ...rest
}: TakerHistoryArgs) => {
  // default to deposit date, updating fallback from pay date to deposit date now that good funds date project is fully released
  const { currency, sort, dateType = 'depositDate' } = rest;

  const sortOrder = sort.startsWith('-') ? 'desc' : 'asc';

  // convert sort as string to SEASupplierMarketClearsSortParams
  const sortField = sort.startsWith('-') ? sort.slice(1) : sort;

  const validSortFields: Omit<SEASupplierMArketClearsSortParams, 'undefined'>[] = [
    'makerOrganizationName',
    'payDate',
    'depositDate',
    'clearedInvoiceAmount',
    'clearedDiscountedAmount',
    'clearedDiscountWeightedAvg',
    'clearedEarn',
    'clearedDpeWeightedAvg',
  ];

  const typedSortField = validSortFields.includes(sortField)
    ? (sortField as SEASupplierMArketClearsSortParams)
    : 'depositDate'; // fallback to deposit date as default, updating fallback from pay date to deposit date now that good funds date project is fully released

  const searchParams = qs.stringify(
    {
      ...(currency && { toCurrency: currency }),
      // use deposit date as date range if dateType is depositDate, otherwise use pay date
      ...(startDate && (dateType === 'depositDate' ? { startDepositDate: startDate } : { startPayDate: startDate })),
      ...(endDate && (dateType === 'depositDate' ? { endDepositDate: endDate } : { endPayDate: endDate })),
      ...(pendingClear !== undefined && { isPendingClear: pendingClear ? 'true' : 'false' }),
      ...(makerOrganizationUuids &&
        makerOrganizationUuids.length > 0 && { makerOrganizationUuids: [...new Set(makerOrganizationUuids)] }),
      page,
      limit,
      sort: typedSortField,
      sortOrder,
    } satisfies SEASupplierMarketClearsParams,
    { arrayFormat: 'repeat' } // 'repeat' format produces makerOrganizationUuids=id1&makerOrganizationUuids=id2, which is parsed correctly by both Express 4 (qs/extended) and Express 5 (Node querystring/simple) without any server-side override
  );

  const supplierMarketClearsResponse = await apiClient
    .get(`api/supplier-experience/supplier-market-clears`, {
      searchParams: searchParams,
    })
    .json<SEASupplierMarketClearsResponse>();

  return supplierMarketClearsResponse?.data.clears || [];
};

const useTakerHistory = (params: TakerHistoryArgs, options?: Partial<UseQueryOptions<TakerHistory[]>>) => {
  const shouldRunQuery = typeof options?.enabled === 'boolean' ? options.enabled : true;

  const results = useQueries({
    queries: [
      {
        queryKey: ['taker-markets', { includeInactive: true }], // update query key to include includeInactive flag
        queryFn: () => fetchTakerMarketsWithoutUserSuppliers({ includeInactive: true }), // always include inactive markets
        placeholderData: keepPreviousData,
        enabled: shouldRunQuery,
      },
      {
        queryKey: ['credit-notes'],
        queryFn: fetchCreditNotes,
        placeholderData: keepPreviousData,
      },
      {
        queryKey: ['taker-history', params],
        queryFn: () => fetchTakerHistory(params),
        placeholderData: keepPreviousData,
        enabled: shouldRunQuery,
      },
    ],
  });

  const [takerMarketsResult, creditNotesResult, historyResult] = results;

  // Handle fetching states
  const isFetching = results.some((result) => result.isFetching);

  // Return loading state if any query is fetching and we don't have data yet
  if (isFetching && !historyResult.data) {
    return {
      data: undefined,
      isFetching,
    };
  }

  // All queries successful, combine the data
  const takerMarkets = (takerMarketsResult.data as TakerMarket[]) ?? [];
  const creditNotesData = creditNotesResult.data ?? [];
  const historyData = historyResult.data as RawTakerHistoryData[] | undefined;

  if (!historyData) {
    return {
      data: [],
      isFetching,
    };
  }

  // Extract markets with credit notes from credit notes data
  const marketsWithCreditNotes = extractMarketsWithCreditNotes(creditNotesData as CreditNotesByMakerOrg[]);

  // Transform SEA API data - cast to SupplierMarketHistory since we know it's from SEA API
  const transformedData: TakerHistory[] = (historyData as SupplierMarketHistory[]).map((item) => {
    // Find the corresponding taker market for the clear to enrich the data
    const takerMarket = takerMarkets.find(
      (tm: TakerMarket) =>
        tm.marketUuid === item.supplierMarket.marketUuid &&
        tm.takerDivisionUuid === item.supplierMarket.supplierDivisionUuid &&
        tm.makerOrganizationUuid === item.supplierMarket.makerOrganizationUuid
    );

    return transformSupplierMarketClearToTakerHistory(item, takerMarket, marketsWithCreditNotes);
  });

  return {
    data: transformedData,
    isFetching,
  };
};

export default useTakerHistory;
